import { useEffect, useState } from "react";
import type { EstadoVault } from "../hooks/useVault";
import type { EsperaKeeper } from "../hooks/useEsperaKeeper";
import { num } from "../lib/formato";
import { useT } from "../lib/i18n";

/**
 * CAPITAL PENDIENTE DE COLOCAR (2026-10-14).
 *
 * Al añadir capital o al reinvertir ganancias el dinero entra al VAULT, no a la posición: queda
 * suelto hasta que el robot lo equilibra y lo barre al rango (`sweep`), en su siguiente ronda. Sin
 * esto el dueño ve que su capital "no subió" y no sabe si se perdió o cuándo entra.
 *
 * La cifra sale de la cadena con la MISMA cuenta que el contrato (`_deployable`) y el keeper:
 *     saldo suelto − bolsa de gas − ganancia apartada − reserva (en el token de referencia)
 * El cuándo lo cuenta el keeper (`barrido` en keeper.<red>.json); si no lo publicó aún —recién
 * añadido— se da la próxima ronda, que es cuando lo va a mirar.
 */
function useAhora(cada = 15_000) {
  const [ahora, setAhora] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setAhora(Math.floor(Date.now() / 1000)), cada);
    return () => clearInterval(id);
  }, [cada]);
  return ahora;
}

const SWEEP_MIN = 0.5;

export function CapitalPendiente({ v, espera }: { v: EstadoVault; espera?: EsperaKeeper | null }) {
  const t = useT();
  const ahora = useAhora();
  if (!v.abierto) return null;

  const h = (x: bigint, d: number) => Number(x) / 10 ** d;
  const reservaH = h(v.reserva, v.ref.decimals);
  const dep0 = Math.max(0, v.libre.a0 - h(v.gasBudget0, v.token0.decimals) - h(v.profit0, v.token0.decimals)
    - (v.refEsToken0 ? reservaH : 0));
  const dep1 = Math.max(0, v.libre.a1 - h(v.gasBudget1, v.token1.decimals) - h(v.profit1, v.token1.decimals)
    - (v.refEsToken0 ? 0 : reservaH));
  const enRef = (a0: number, a1: number) =>
    v.refEsToken0 ? a0 + (v.precio > 0 ? a1 / v.precio : 0) : a1 + a0 * v.precio;
  const pendiente = enRef(dep0, dep1);
  // Restos de redondeo de cada swap (céntimos) no son "capital pendiente": no se enseñan.
  if (pendiente <= 0 || pendiente < Math.max(v.objetivo * 0.0005, 1e-6)) return null;

  const b = espera?.barrido;
  const minimo = b?.minimo ?? SWEEP_MIN;
  const ronda = espera?.ronda ?? 300;
  const pct = v.objetivo > 0 ? (pendiente / v.objetivo) * 100 : null;

  const enMin = (ts?: number | null) => (ts ? Math.max(0, Math.round((ts - ahora) / 60)) : null);
  const cuando = (m: number | null) =>
    m == null ? t("en la próxima revisión del robot (cada {r} min)", { r: String(Math.round(ronda / 60)) })
      : m < 1 ? t("en breve, en esta misma revisión")
      : t("en ~{m} min", { m: String(m) });
  const hora = (ts: number) => new Date(ts * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // Qué pasa y cuándo. `tono`: ok = va a entrar solo; aviso = espera algo; error = necesita al dueño.
  let tono: "ok" | "aviso" | "error" = "ok";
  let titulo: string;
  let cuerpo: string;
  const estado = !v.enRango ? "fuera_rango" : b?.estado ?? (pendiente < minimo ? "bajo_minimo" : "nuevo");
  switch (estado) {
    case "barriendo":
      titulo = t("Colocándose ahora");
      cuerpo = t("El robot está equilibrando los dos tokens y metiéndolos en tu rango. En unos segundos aparece dentro de la posición.");
      break;
    case "parcial":
      titulo = t("Colocándose por tramos");
      cuerpo = t("La pool es fina y meterlo de golpe movería el precio en tu contra. El robot coloca un tramo en cada revisión (cada {r} min) hasta terminar; el siguiente {c}.",
        { r: String(Math.round(ronda / 60)), c: cuando(enMin(espera?.proxima)) });
      break;
    case "colocado":
      titulo = t("Ya colocado");
      cuerpo = t("El robot acaba de meterlo en la posición; la cifra se actualiza en unos segundos.");
      break;
    case "espera":
    case "reintento": {
      tono = "aviso";
      const sig = b?.siguiente ?? espera?.proxima ?? null;
      titulo = t("Reintentando");
      cuerpo = sig
        ? t("El último intento no pudo colocarlo (precio o simulación). Lo vuelve a intentar a las {h} ({c}).", { h: hora(sig), c: cuando(enMin(sig)) })
        : t("El último intento no pudo colocarlo (precio o simulación). Lo vuelve a intentar {c}.", { c: cuando(enMin(espera?.proxima)) });
      break;
    }
    case "sin_gas":
      tono = "error";
      titulo = t("Esperando gas");
      cuerpo = t("La bolsa de gas no alcanza para el barrido, y el robot no adelanta gas. Recarga la bolsa y entra en la siguiente revisión.");
      break;
    case "bajo_minimo":
      tono = "aviso";
      titulo = t("Se coloca en el próximo reajuste");
      cuerpo = t("Es menos de {m} {s}: barrerlo ahora costaría más gas de lo que se coloca. Queda en el vault (sigue siendo tuyo) y entra solo en el próximo reajuste del rango.",
        { m: num(minimo, 2), s: v.ref.symbol });
      break;
    case "fuera_rango":
    case "con_reajuste":
      tono = "aviso";
      titulo = t("Se coloca en el próximo reajuste");
      cuerpo = !v.enRango
        ? t("Tu posición está fuera de rango y ahora solo admite uno de los dos tokens. Cuando el robot re-centre el rango, este capital entra junto con el resto.")
        : t("En esta piscina el robot solo puede meterlo re-centrando el rango, y no compensa hacerlo solo por esta cantidad. Entra en el próximo reajuste.");
      break;
    default:
      titulo = t("En cola para entrar");
      cuerpo = t("El robot lo detecta y lo mete en tu rango {c}. Equilibra los dos tokens con un swap y lo añade a la posición, sin mover el rango.",
        { c: cuando(enMin(espera?.proxima)) });
  }

  const partes = [
    dep0 > 0 ? `${num(dep0)} ${v.token0.symbol}` : null,
    dep1 > 0 ? `${num(dep1)} ${v.token1.symbol}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className={`capital-pendiente ${tono}`} role="status" aria-live="polite">
      <div className="cp-cab">
        <div>
          <span className="k">{t("Capital pendiente de colocar")}</span>
          <span className="v">{num(pendiente, 2)} <i>{v.ref.symbol}</i></span>
          <span className="reparto">{partes}{pct != null && ` · ${num(pct, 2)}% ${t("de tu capital")}`}</span>
        </div>
        <span className={`pill ${tono === "ok" ? "nuevo" : "aviso"}`}>{titulo}</span>
      </div>
      <p className="cp-cuerpo">{cuerpo}</p>
      <p className="cp-nota">
        {t("Al añadir capital o reinvertir ganancias el dinero entra primero al vault y luego el robot lo pasa a la posición. Mientras tanto no cobra comisiones, pero sigue siendo tuyo y sale entero si retiras.")}
      </p>
    </div>
  );
}
